import { useState } from "react";
import { Search, User, Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useScrollDirection } from "@/hooks/useScrollDirection";
import { cn } from "@/lib/utils";

export function MobileHeader() {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();
  const { wishlistItems } = useWishlist();
  const wishlistCount = wishlistItems.length;
  const isMobile = useIsMobile();
  const handleScrollToTop = useScrollToTop();
  const { scrollDirection } = useScrollDirection();

  // Only show on mobile
  if (!isMobile) return null;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    navigate(`/search-results?q=${encodeURIComponent(q)}`);
  };

  // When scrolling down, top bar hides, so header should stick to top
  const isTopBarHidden = scrollDirection === "down";
  const isSticky = isTopBarHidden;

  return (
    <header 
      className={`sticky z-40 backdrop-blur border-b transition-all duration-150 ease-out ${
        isSticky 
          ? "top-0 bg-primary border-primary/20" 
          : "top-12 bg-background/95 border-border"
      }`}
    >
      <div className="flex items-center gap-3 p-4 h-16">
        {/* Logo */}
        <div 
          className={`font-display font-bold text-lg cursor-pointer flex-shrink-0 transition-colors duration-300 ${
            isSticky ? "text-primary-foreground" : "text-primary"
          }`}
          onClick={() => handleScrollToTop("/")}
        >
          PERLLA
        </div>

        {/* Search Bar */}
        <form className="flex-1" onSubmit={handleSearch}>
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors duration-300 ${
              isSticky ? "text-primary-foreground/70" : "text-muted-foreground"
            }`} />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-10 border-0 h-10 transition-colors duration-300 ${
                isSticky 
                  ? "bg-primary-foreground/10 text-primary-foreground placeholder:text-primary-foreground/70" 
                  : "bg-muted/50 text-foreground placeholder:text-muted-foreground"
              }`}
            />
          </div>
        </form>

        {/* Wishlist */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/account/wishlist")}
          className={`flex-shrink-0 relative transition-colors duration-300 ${
            isSticky 
              ? "text-primary-foreground hover:bg-primary-foreground/10" 
              : "text-foreground hover:bg-accent"
          }`}
        >
          <Heart 
            className={cn(
              "h-5 w-5 transition-colors",
              wishlistCount > 0 && (isSticky ? "fill-primary-foreground" : "fill-primary text-primary")
            )} 
          />
          {wishlistCount > 0 && (
            <span className={`absolute -top-1 -right-1 text-xs rounded-full h-5 w-5 flex items-center justify-center transition-colors duration-300 ${
              isSticky 
                ? "bg-primary-foreground text-primary" 
                : "bg-primary text-primary-foreground"
            }`}>
              {wishlistCount}
            </span>
          )}
        </Button>

        {/* Profile */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(user ? "/account" : "/auth")}
          className={`flex-shrink-0 transition-colors duration-300 ${
            isSticky 
              ? "text-primary-foreground hover:bg-primary-foreground/10" 
              : "text-foreground hover:bg-accent"
          }`}
        >
          <User className="h-5 w-5" />
        </Button>
      </div>
    </header>
  );
}